import React from "react";
import { motion } from "framer-motion";
import { MessageCircle, MessageSquare, Smartphone, Mail, Phone } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import SectionBadge from "@/components/SectionBadge";

const channels = [
  {
    id: "whatsapp",
    label: "WhatsApp",
    Icon: MessageCircle,
    color: "#25D366",
    glow: "rgba(37,211,102,0.45)",
  },
  { id: "sms", label: "SMS", Icon: MessageSquare, color: "#1dc7e9", glow: "rgba(29,199,233,0.45)" },
  { id: "rcs", label: "RCS", Icon: Smartphone, color: "#1dc7e9", glow: "rgba(29,199,233,0.45)" },
  { id: "email", label: "E-mail", Icon: Mail, color: "#1dc7e9", glow: "rgba(29,199,233,0.45)" },
  { id: "voice", label: "Voz", Icon: Phone, color: "#1dc7e9", glow: "rgba(29,199,233,0.45)" },
];

const ChannelsSection = () => {
  const { t } = useLang();

  return (
    <section
      id="canais"
      data-testid="channels-section"
      className="relative py-24 lg:py-28 bg-[#0b1220]/40 border-y border-[#1dc7e9]/10"
    >
      <div
        aria-hidden
        className="absolute inset-0 bg-dots opacity-25 pointer-events-none"
      />
      <div
        aria-hidden
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[900px] h-[500px] glow-radial opacity-50 pointer-events-none"
      />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <div className="max-w-3xl mx-auto text-center mb-14">
          <SectionBadge>{t.channels.eyebrow}</SectionBadge>
          <h2 className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-bold leading-[1.08] tracking-tight text-white text-balance">
            {t.channels.title}
          </h2>
          <p className="mt-5 text-[#b8c0cc] leading-relaxed">
            {t.channels.subtitle}
          </p>
        </div>

        <div className="relative">
          {/* Animated connecting line (desktop) */}
          <svg
            className="absolute left-0 right-0 top-[42%] -translate-y-1/2 hidden md:block pointer-events-none"
            viewBox="0 0 1000 60"
            preserveAspectRatio="none"
            width="100%"
            height="60"
            aria-hidden
          >
            <defs>
              <linearGradient id="chl" x1="0" x2="1">
                <stop offset="0%" stopColor="#1dc7e9" stopOpacity="0" />
                <stop offset="50%" stopColor="#1dc7e9" stopOpacity="0.7" />
                <stop offset="100%" stopColor="#1dc7e9" stopOpacity="0" />
              </linearGradient>
            </defs>
            <path
              d="M0 30 Q 250 0 500 30 T 1000 30"
              stroke="url(#chl)"
              strokeWidth="1.6"
              fill="none"
              strokeDasharray="4 6"
              className="animate-dash"
            />
          </svg>

          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-8 md:gap-3 relative">
            {channels.map((c, i) => {
              const Icon = c.Icon;
              return (
                <motion.div
                  key={c.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.4 }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  className="flex flex-col items-center gap-4 group"
                  data-testid={`channel-${c.id}`}
                >
                  <div className="relative">
                    <div
                      aria-hidden
                      className="absolute inset-0 rounded-full blur-2xl opacity-60 group-hover:opacity-100 transition-opacity duration-500"
                      style={{ background: c.glow }}
                    />
                    <div
                      className="relative w-20 h-20 md:w-24 md:h-24 rounded-full flex items-center justify-center transition-all duration-300 group-hover:scale-110"
                      style={{
                        background: `radial-gradient(circle at 30% 30%, ${c.color} 0%, ${c.color}cc 60%, ${c.color}aa 100%)`,
                        boxShadow: `0 12px 40px ${c.glow}, inset 0 1px 0 rgba(255,255,255,0.25), inset 0 -8px 16px rgba(0,0,0,0.3)`,
                      }}
                    >
                      <Icon size={32} strokeWidth={1.8} className="text-white drop-shadow" />
                    </div>
                  </div>
                  <div className="text-base font-bold text-white tracking-tight">
                    {c.label}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
};

export default ChannelsSection;
